/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import ApiService from "../api-service";

class FileApiService extends ApiService {
  requestCache = new Map();
  REQUEST_CACHE_TTL_MS = 10 * 60 * 1000;
  GHS_STORAGE_KEY = "files_ghs_catalog";
  PRECAUTIONARY_STORAGE_KEY = "files_precautionary_catalog";

  stableStringify = (value) => {
    if (value == null) return String(value);
    if (Array.isArray(value)) {
      return `[${value.map((item) => this.stableStringify(item)).join(",")}]`;
    }
    if (typeof value === "object") {
      const keys = Object.keys(value).sort();
      return `{${keys
        .map((key) => `${JSON.stringify(key)}:${this.stableStringify(value[key])}`)
        .join(",")}}`;
    }
    return JSON.stringify(value);
  };

  cloneData = (data) => {
    if (typeof structuredClone === "function") {
      return structuredClone(data);
    }
    return JSON.parse(JSON.stringify(data));
  };

  getCacheKey = (endpoint, params = {}) =>
    `${endpoint}|${this.stableStringify(params)}`;

  invalidateRequestCache = () => {
    this.requestCache.clear();
  };

  invalidateCache = (endpoint) => {
    this.requestCache.forEach((value, key) => {
      if (key.startsWith(endpoint)) {
        this.requestCache.delete(key);
      }
    });
  }

  getCachedList = async (endpoint, params = {}) => {
    const key = this.getCacheKey(endpoint, params);
    const cached = this.requestCache.get(key);
    const now = Date.now();
    if (cached && cached.expiresAt > now) {
      return this.cloneData(cached.data);
    }
    const data = await this.get(endpoint, params).then((res) => res.data);
    this.requestCache.set(key, {
      data,
      expiresAt: now + this.REQUEST_CACHE_TTL_MS,
    });
    return this.cloneData(data);
  };

  getHistory = async (params = {}) =>
    await this.getCachedList("/archive", params);

  /**
   * Fetch distinct values for a filter column.
   * @param {"archive"|"imported"|"hp-finder"} resource
   * @param {{ column: string, q?: string, limit?: number }} params
   * @returns {Promise<string[]>}
   */
  getDistinctValues = async (resource, params = {}) => {
    const data = await this.getCachedList(`/${resource}/distinct-values`, params);
    return Array.isArray(data?.values) ? data.values : [];
  };

  /** Field-level history list for document/item and column. Returns an array of dictionaries. */
  getDocumentFieldChanges = async (entityId, columnName) =>
    await this.getCachedList(`/document-log/${entityId}/${columnName}`);

  getItemFieldChanges = async (entityId, columnName) =>
    await this.getCachedList(`/item-log/${entityId}/${columnName}`);

  deleteHistory = async (id) =>
    await this.delete(`/archive/${id}`).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  deleteHistoryBulk = async (ids = [], filters = null) => {
    if (!ids?.length && !filters) return;
    const body = filters ? { ids: [], filters } : { ids };
    await this.post("/archive/bulk_destroy/", body, true).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });
  };

  exportHistory = async (params = {}) =>
    await this.get("/archive/export", params, false, {
      responseType: "blob",
    }).then((res) => res.data);

  updateHistory = async (id, data = {}) =>
    await this.patch(`/archive/${id}`, data, false).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  /** Duplicate history records to parsed-files. Pass ids or filters (for "select all in backend"). */
  duplicateHistoryToParsed = async (ids = [], filters = null) => {
    const body = filters ? { filters } : { id: ids };
    return this.post(`/archive/bulk_duplicate_to_parsed`, body, true).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });
  };

  /** Move a single history record back to parsed-files (revert). */
  moveHistoryToParsed = async (id) =>
    await this.post(`/archive/${id}/move_to_parsed`, {}, true).then((res) => {
      this.invalidateRequestCache();
      this.invalidateCache("/counter");
      return res.data;
    });

  /** Move all history records matching filters to parsed-files (bulk). */
  bulkMoveHistoryToParsed = async (filters) =>
    await this.post("/archive/bulk_move_to_parsed/", { filters }, true).then((res) => {
      this.invalidateRequestCache();
      this.invalidateCache("/counter");
      return res.data;
    });

  history = async (data = {}) => {
    return await this.post("/imported/history", data, true, false, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });
  };

  getUploads = async (params = {}) =>
    await this.getCachedList("/upload", params);

  upload = async (data = {}) =>
    await this.post("/upload", data, false).then((res) => {
      this.invalidateRequestCache();
      this.invalidateCache("/counter");
      return res.data;
    });

  // Last line of defence against a duplicate DELETE for the same document: callers
  // guard their own click handlers, but if two ever overlap, the second gets the
  // first's promise instead of a second request (which would 404, the row being gone
  // already). Scoped to the request's lifetime - not a time window - so a deliberate
  // re-delete after this one settles still goes through.
  inFlightUploadDeletes = new Map();

  deleteUpload = async (id) => {
    const pending = this.inFlightUploadDeletes.get(id);
    if (pending) return await pending;

    const request = this.delete(`/upload/${id}`)
      .then((res) => {
        this.invalidateRequestCache();
        this.invalidateCache("/counter");
        return res.data;
      })
      .finally(() => {
        this.inFlightUploadDeletes.delete(id);
      });

    this.inFlightUploadDeletes.set(id, request);
    return await request;
  };

  confirmUpload = async () =>
    await this.post("/upload/confirm").then((res) => {
      this.invalidateRequestCache();
      this.invalidateCache("/counter");
      return res.data;
    });

  updateUploadEerCodes = async (id, data) =>
    await this.post(`/upload/${id}/eer`, data, false).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  removeUploadEerCode = async (id, eerCode) =>
    await this.delete(`/upload/${id}/eer/${eerCode}`).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  // Copies sourceId's EER codes onto other uploads server-side in a single
  // request. Omitting targetIds applies it to every upload of the user, not
  // just the pages the table has loaded so far.
  copyUploadEerCodes = async (sourceId, targetIds = []) =>
    await this.post("/upload/eer/copy", {
      source_id: sourceId,
      ...(targetIds?.length ? { target_ids: targetIds } : {}),
    }).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  getParsedData = async (params = {}) =>{
    var data = await this.getCachedList("/imported", params);
    this.invalidateCache("/counter");
    return data;
  }

  updateParsedData = async (id, data = {}, item_detail_id) =>
    await this.patch(
      `/imported/${id}`,
      data,
      false,
      item_detail_id ? { item_detail_id } : undefined
    ).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  addParsedData = async (data) =>
    await this.post(`/imported`, data).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  deleteParsedData = async (id) =>
    await this.delete(`/imported/${id}`).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  addParsedDataItem = async (id, data) =>
    await this.post(`/imported/${id}/item`, data).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  deleteParsedDataItem = async (id, itemId) =>
    await this.delete(`/imported/${id}/item/${itemId}`).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  sendEmail = async (language = "en") =>
    await this.post("/imported/send-email", { language }).then((res) => res.data);
  sendValidatedEmail = async (eer_codes, language = "en") =>
    await this.post("/hp-finder/send_email", { language }, false, false, {
      params: { eer_codes },
    }).then((res) => res.data);

  export = async (params = {}) =>
    await this.get("/imported/export", params, false, {
      responseType: "blob",
    }).then((res) => res.data);

  validate = async (params, eer_codes) =>
    await this.post("/imported/validate", params, true, false, {
      params: { eer_codes },
    }).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  proceed = async (id = [], eer_codes) =>
    await this.post("/hp-finder/proceed", { id }, false, false, {
      params: { eer_codes },
    }).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  checkTaskStatus = async (taskId) =>
    await this.get(`/common/task_status/${taskId}`).then((res) => res.data);

  addEerCode = async (id, eerCode) =>
    await this.post(`/imported/${id}/eer/${eerCode}`).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  removeEerCode = async (id, eerCode) =>
    await this.delete(`/imported/${id}/eer/${eerCode}`).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  getValidatedFiles = async (params = {}) =>
    await this.getCachedList("/hp-finder", params);

  updateValidatedFiles = async (id, data = {}, item_detail_id) =>
    await this.patch(
      `/hp-finder/${id}`,
      data,
      false,
      item_detail_id ? { item_detail_id } : undefined
    ).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  deleteValidatedFiles = async (id) =>
    await this.delete(`/hp-finder/${id}`).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  bulkDeleteValidatedFiles = async (data = {}, eer_codes) =>
    await this.post("/hp-finder/bulk-delete", data, true, false, {
      params: { eer_codes },
    }).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  bulkDeleteParsedData = async (data = {}) =>
    await this.post("/imported/bulk-delete", data).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  validatedExport = async (params = {}, eer_codes) =>
    await this.post("/hp-finder/export", params, true, false, {
      params: { eer_codes },
      responseType: "blob",
    }).then((res) => res.data);

  validatedProceed = async (id = [], eer_codes, customer = null) =>
    await this.post(
      "/hp-finder/proceed",
      { id, ...(customer != null && { customer }) },
      true,
      false,
      { params: { eer_codes } }
    ).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  validatedRevert = async (params, eer_codes) =>
    await this.post("/hp-finder/revert", params, true, false, {
      params: { eer_codes },
    }).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  getFilesCount = async () =>
    await this.getCachedList("/counter");

  getAnalytics = async (userId = null) => {
    const params = userId ? { user_id: userId } : {};
    const res = await this.get("/analytics", params);
    return res.data;
  };

  getAnalyticsDetail = async (metric, code = null, userId = null) => {
    const params = { metric, ...(code ? { code } : {}), ...(userId ? { user_id: userId } : {}) };
    const res = await this.get("/analytics/detail", params);
    return res.data;
  };

  exportAnalytics = async (format, userId = null) => {
    const params = { export_format: format, ...(userId ? { user_id: userId } : {}) };
    return await this.get("/analytics/export", params, false, { responseType: "blob" }).then((res) => res.data);
  };

  validateConstraint = async (params, eer_codes) =>
    await this.post("/imported/validate-constraint", params, true, false, {
      params: { eer_codes },
    }).then((res) => {
      this.invalidateRequestCache();
      return res.data;
    });

  getHazardPoints = async () => await this.get("/hp").then((res) => res.data);

  normalizeGhsCatalog = (value) => {
    if (!Array.isArray(value)) return [];
    return value
      .filter((item) => item && item.id != null)
      .map((item) => ({
        id: item.id,
        code: item.code || "",
        description: item.description || "",
        attachment: item.attachment || "",
      }));
  };

  getStoredGhsCatalog = () => {
    try {
      const raw = localStorage.getItem(this.GHS_STORAGE_KEY);
      if (!raw) return [];
      return this.normalizeGhsCatalog(JSON.parse(raw));
    } catch (_error) {
      return [];
    }
  };

  setStoredGhsCatalog = (list = []) => {
    try {
      localStorage.setItem(
        this.GHS_STORAGE_KEY,
        JSON.stringify(this.normalizeGhsCatalog(list))
      );
    } catch (_error) {
      // Ignore storage errors and continue with in-memory data.
    }
  };

  clearGhsCatalogCache = () => {
    try {
      localStorage.removeItem(this.GHS_STORAGE_KEY);
    } catch (_error) {
      // Ignore storage errors and continue cache invalidation.
    }
    this.invalidateCache("/ghs");
  };

  getGhsCatalog = async ({ forceRefresh = false } = {}) => {
    if (!forceRefresh) {
      const localCatalog = this.getStoredGhsCatalog();
      if (localCatalog.length > 0) {
        return this.cloneData(localCatalog);
      }
    }

    const remoteCatalog = await this.getCachedList("/ghs");
    const normalizedCatalog = this.normalizeGhsCatalog(remoteCatalog);
    this.setStoredGhsCatalog(normalizedCatalog);
    return this.cloneData(normalizedCatalog);
  };

  prefetchGhsCatalog = async () => {
    try {
      return await this.getGhsCatalog();
    } catch (_error) {
      return [];
    }
  };

  normalizePrecautionaryCatalog = (value) => {
    if (!Array.isArray(value)) return [];
    return value
      .filter((item) => item && item.code != null)
      .map((item) => ({
        code: String(item.code),
        description: item.description || "",
      }));
  };

  getStoredPrecautionaryCatalog = () => {
    try {
      const raw = localStorage.getItem(this.PRECAUTIONARY_STORAGE_KEY);
      if (!raw) return [];
      return this.normalizePrecautionaryCatalog(JSON.parse(raw));
    } catch (_error) {
      return [];
    }
  };

  setStoredPrecautionaryCatalog = (list = []) => {
    try {
      localStorage.setItem(
        this.PRECAUTIONARY_STORAGE_KEY,
        JSON.stringify(this.normalizePrecautionaryCatalog(list))
      );
    } catch (_error) {
      // Ignore storage errors and continue with in-memory data.
    }
  };

  clearPrecautionaryCatalogCache = () => {
    try {
      localStorage.removeItem(this.PRECAUTIONARY_STORAGE_KEY);
    } catch (_error) {
      // Ignore storage errors and continue cache invalidation.
    }
    this.invalidateCache("/p");
  };

  getPrecautionaryCatalog = async ({ forceRefresh = false } = {}) => {
    if (!forceRefresh) {
      const localCatalog = this.getStoredPrecautionaryCatalog();
      if (localCatalog.length > 0) {
        return this.cloneData(localCatalog);
      }
    }

    const remoteCatalog = await this.getCachedList("/p");
    const normalizedCatalog = this.normalizePrecautionaryCatalog(remoteCatalog);
    this.setStoredPrecautionaryCatalog(normalizedCatalog);
    return this.cloneData(normalizedCatalog);
  };

  prefetchPrecautionaryCatalog = async () => {
    try {
      return await this.getPrecautionaryCatalog();
    } catch (_error) {
      return [];
    }
  };
}

export const FileApi = new FileApiService("files");
